import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScheduleBookingForm from "../components/landing/ScheduleBookingForm";
import Footer from "../components/landing/Footer";
import LandingNavbar from "../components/landing/LandingNavbar";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { OUTBOUND_TRIGGERS_FAQS } from "../data/seo/faqs";
import { buildServiceSchema } from "../data/seo/structuredData";

const triggerSources = [
    {
        title: "Webhook",
        description: "Any external system can POST a payload that fires a personalized outbound call. Bring your own auth, retry, and idempotency.",
    },
    {
        title: "Shopify Event",
        description: "Order created, abandoned cart, refund issued, fulfillment updated — call the customer the moment it matters.",
    },
    {
        title: "Time-Relative",
        description: "Schedule calls relative to any field — e.g. 24 hours before an appointment, or 3 days after signup. Cadences with retry rules built in.",
    },
    {
        title: "Internal Event",
        description: "Trigger a callback whenever a workflow, support ticket, or pipeline emits an internal event from anywhere in your stack.",
    },
    {
        title: "Data Collection Watch",
        description: "When a row in a watched collection changes (status, score, tag), the matching contact gets a call automatically.",
    },
    {
        title: "Scheduled List",
        description: "Upload a CSV or sync a list — the agent dials it on a cadence with retry rules, opt-out checks, and consent built in.",
    },
];

const keyFeatures = [
    {
        title: "One Pipeline, Six Sources",
        description: "Whatever fires the call — webhook, Shopify event, schedule — the same Call Pipeline answers. Build once, dial from everywhere.",
    },
    {
        title: "Retry & Backoff",
        description: "Automatic retry on no-answer or busy with configurable backoff. Cap retries per contact to stay compliant.",
    },
    {
        title: "Compliance Built-In",
        description: "DLT registration for India, opt-out lists, quiet hours per region, and audit logs out of the box.",
    },
    {
        title: "Smart Country Routing",
        description: "India routes through Jio + MSG91, US/CA/GB/AU/IE/NZ through a managed number or your own Twilio account. Picked automatically per contact.",
    },
    {
        title: "Personalized Per Contact",
        description: "Every payload variable becomes a prompt variable — the agent uses the customer's name, order, appointment, and history.",
    },
    {
        title: "Concurrent Dialing",
        description: "Dial hundreds of contacts in parallel with concurrency caps, throttle limits, and per-region rate-limiting.",
    },
    {
        title: "Live Dashboard & Logs",
        description: "Watch every outbound campaign live. Drill into any call's transcript, pipeline path, and timing — with replay audio.",
    },
    {
        title: "Webhooks Out",
        description: "Outcome webhooks fire after every call: booked, no-show, transferred, opted-out — wire it back into your CRM or queue.",
    },
    {
        title: "SMS Fallback",
        description: "If a call goes unanswered, automatically fall back to SMS with a personalized message and a callback link.",
    },
];

const useCases = [
    {
        title: "Abandoned Cart Recovery",
        description: "Shopify event fires the moment a cart is abandoned for 30 minutes. The agent calls, answers questions, and offers a discount.",
    },
    {
        title: "Appointment Reminders",
        description: "Time-relative trigger calls every patient 24 hours before their appointment. The agent confirms, reschedules, or cancels.",
    },
    {
        title: "Lead Speed-To-Call",
        description: "Webhook from your form fires the second a lead submits. The agent calls within 60 seconds and qualifies them live.",
    },
    {
        title: "Renewal Outreach",
        description: "Data Collection watch on contracts expiring in 30 days. The agent calls each customer and books a renewal slot.",
    },
];

const primaryKeywords = [
    "Outbound AI calls",
    "AI dialer",
    "Voice agent for outbound campaigns",
    "Webhook to phone call",
    "Shopify abandoned cart call",
    "Automated appointment reminders",
    "Lead speed to call AI",
    "AI cold call automation",
];

export default function OutboundTriggersMarketingPage() {
    usePageSeo({
        title: "AI Outbound Triggers — Webhooks, Shopify, Schedules → Calls | FloGPT",
        description:
            "Six ways to dial out automatically — webhook, Shopify event, time-relative cadence, internal event, data-collection watch, or scheduled list. The same Call Pipeline handles every outbound campaign.",
        keywords: primaryKeywords.join(", "),
        canonical: "/ai-outbound-triggers",
    });

    const serviceSchema = useMemo(
        () =>
            buildServiceSchema({
                name: "FloGPT AI Outbound Trigger Engine",
                description:
                    "Automate outbound AI phone calls from webhooks, Shopify events, time-relative cadences, internal events, data-collection watches, and scheduled call lists — with quiet hours, retries, and DLT/TCPA-aware compliance.",
                path: "/ai-outbound-triggers",
                serviceType: "Outbound AI Call Automation",
                features: [
                    "Webhook-triggered calls from any system",
                    "Shopify event triggers (abandoned cart, fulfillment, refund)",
                    "Time-relative cadences (e.g. 24h after no-show)",
                    "Internal event subscriptions",
                    "Data-collection row watches",
                    "Scheduled list (CSV) campaigns",
                    "Quiet hours, max attempts, time-zone aware",
                    "DLT-registered for India, TCPA-aware for US",
                ],
            }),
        [],
    );

    return (
        <div className="bg-slate-950 text-slate-100 min-h-screen overflow-x-hidden">
            <LandingNavbar />

            <main>
                {/* HERO */}
                <section className="relative overflow-hidden border-b border-slate-900 pt-24 sm:pt-28" id="overview">
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-orange-500/20 blur-3xl" />
                        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-pink-500/10 blur-3xl" />
                        <div className="absolute left-0 bottom-0 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300 mb-6">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                    Outbound Triggers · 6 Sources · 1 Pipeline
                                </div>
                                <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-[3.35rem] lg:leading-[1.05]">
                                    Six Ways the Agent{" "}
                                    <motion.span
                                        className="inline-block"
                                        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                        style={{
                                            background: "linear-gradient(90deg, #f97316, #ec4899, #3b82f6, #f97316)",
                                            backgroundSize: "200% 100%",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                        }}
                                    >
                                        Dials Out
                                    </motion.span>{" "}
                                    — Automatically
                                </h1>
                                <p className="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed">
                                    Plug your business events into the Call Widget and let the agent handle the call so your team doesn't have to. Webhooks, Shopify events, schedules, internal events, data-collection watches, and CSV lists — same pipeline, every time.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center gap-4">
                                    <a
                                        href="#book-free-demo"
                                        className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                    >
                                        Book a Free Demo
                                    </a>
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Start Building Free
                                    </Link>
                                </div>
                                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400">
                                    <span>· Retry + backoff</span>
                                    <span>· DLT-compliant</span>
                                    <span>· Concurrency caps</span>
                                </div>
                            </motion.div>

                            {/* Trigger preview */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="relative"
                            >
                                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-orange-500/25 to-pink-500/25 blur-2xl" />
                                <div className="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900/80 shadow-2xl p-6 sm:p-8">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                                                ⚡
                                            </div>
                                            <div>
                                                <div className="text-white font-semibold">Trigger fired · Shopify · abandoned-cart</div>
                                                <div className="text-xs text-slate-400">cart_id: gid://shopify/Cart/47834</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-emerald-300">
                                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                                            Dialing
                                        </div>
                                    </div>
                                    <div className="mt-5 rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-300 font-mono">
                                        {[
                                            "POST /triggers/outbound",
                                            "{",
                                            "  source: 'shopify.cart_abandoned',",
                                            "  pipeline: 'cart-recovery-v4',",
                                            "  contact: { phone, name, cart_value },",
                                            "  retry: { max: 2, backoffMin: 30 },",
                                            "}",
                                        ].map((line, i) => (
                                            <div key={i} className="flex items-start gap-3">
                                                <span className="select-none text-slate-600 w-6 text-right">{i + 1}</span>
                                                <span className="whitespace-pre-wrap">{line}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-4 flex items-center justify-between border-t border-slate-800 pt-4 text-xs text-slate-400">
                                        <span>4 calls in queue · 1 active</span>
                                        <span>Avg. answer 14s</span>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* WHY DIFFERENT */}
                <section className="mx-auto max-w-4xl px-6 lg:px-8 py-14 lg:py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="space-y-6"
                    >
                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                    Stop dialing one-by-one. Stop missing the moment.
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Most outbound campaigns die at attempt #2, and the moments that matter — abandoned cart, lead form, appointment 24h out — go unanswered.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">
                                Outbound Triggers wire your business events directly into a voice agent on your pipeline, so calls happen the second they should — not when a rep finally has bandwidth.
                            </p>
                        </article>

                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                    Why this isn't just another auto-dialer
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Predictive dialers connect calls. They don't have a conversation.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">FloGPT's Outbound Triggers:</p>
                            <ul className="mt-4 space-y-3 text-slate-200">
                                {[
                                    "Run the same multilingual voice agent that answers your inbound calls",
                                    "Personalize every prompt with the trigger payload — name, order, appointment, score",
                                    "Respect quiet hours, opt-outs, retry caps, and DLT/regulatory rules per region",
                                    "Stream outcomes back as webhooks so your CRM / queue stays in sync",
                                ].map((line) => (
                                    <li key={line} className="flex items-start gap-3">
                                        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-orange-400 via-pink-400 to-blue-400 shadow-[0_0_0_3px_rgba(251,146,60,0.15)]" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-4 text-slate-100 font-medium">It's a conversation, not a connection.</p>
                        </article>
                    </motion.div>
                </section>

                {/* TRIGGER SOURCES */}
                <section id="trigger-sources" className="relative overflow-hidden">
                    <div className="absolute inset-0 -z-10 opacity-30">
                        <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-orange-500/30 blur-3xl" />
                        <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-pink-500/20 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                        <div className="text-center max-w-3xl mx-auto">
                            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300 mb-4">
                                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                Six Trigger Sources
                            </div>
                            <h2 className="text-3xl font-bold text-white">Plug any event into a phone call</h2>
                            <p className="mt-4 text-slate-300">
                                Pick the trigger source that fits your event — the agent handles the rest.
                            </p>
                        </div>

                        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {triggerSources.map((source) => (
                                <motion.article
                                    key={source.title}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:border-orange-400/40 hover:bg-white/10 transition-all"
                                >
                                    <h3 className="text-lg font-semibold text-white">{source.title}</h3>
                                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{source.description}</p>
                                </motion.article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* USE CASES */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Real campaigns teams run on day one</h2>
                        <p className="mt-4 text-slate-300">
                            From abandoned-cart recovery to renewal outreach — wire it up in minutes.
                        </p>
                    </div>
                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        {useCases.map((u, idx) => (
                            <motion.article
                                key={u.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.05 }}
                                viewport={{ once: true, amount: 0.2 }}
                                className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20 hover:border-orange-400/30 transition-all"
                            >
                                <h3 className="text-xl font-semibold text-white">{u.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{u.description}</p>
                            </motion.article>
                        ))}
                    </div>
                </section>

                {/* KEY FEATURES */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Everything Outbound Triggers do</h2>
                        <p className="mt-4 text-slate-300">
                            Compliance, retries, concurrency, and personalization — all built in.
                        </p>
                    </div>

                    <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {keyFeatures.map((feature, index) => (
                            <article
                                key={feature.title}
                                className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:border-orange-400/40"
                            >
                                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-500 to-pink-500" />
                                <div className="flex items-center justify-between">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/15 border border-orange-400/30">
                                        <svg className="h-5 w-5 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                </div>

                                <h3 className="mt-5 text-lg font-semibold text-white">{feature.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{feature.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                {/* RELATED LINKS */}
                <section className="mx-auto max-w-5xl px-6 lg:px-8 py-12">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-7">
                        <h3 className="text-lg font-semibold text-white">Pair Outbound Triggers with</h3>
                        <p className="mt-2 text-slate-400 text-sm">Triggers fire calls — these wire up what the agent says and does on each call.</p>
                        <div className="mt-5 grid gap-4 md:grid-cols-3">
                            <Link to="/ai-call-pipeline" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-orange-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Call Pipeline →</div>
                                <p className="mt-1 text-sm text-slate-400">Design exactly what the agent says when each trigger fires.</p>
                            </Link>
                            <Link to="/ai-voice-agents" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-orange-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">
                                    {import.meta.env.VITE_SITE_REGION === "in" ? "Voice Agents Overview →" : "Outbound AI agent triggers"}
                                </div>
                                <p className="mt-1 text-sm text-slate-400">See the full voice product — phone numbers, languages, smart routing.</p>
                            </Link>
                            <Link to="/ai-knowledge-base" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-orange-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Knowledge Base →</div>
                                <p className="mt-1 text-sm text-slate-400">Ground every outbound conversation in your real docs and FAQs.</p>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* CTA + BOOKING */}
                <section id="book-free-demo" className="mx-auto max-w-6xl px-6 lg:px-8 pb-20">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white">See your outbound campaign live</h2>
                        <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                            Book a free 20-minute consultation. We'll wire up a trigger to your stack and call your test number on the call.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="mt-12 max-w-4xl mx-auto"
                    >
                        <ScheduleBookingForm
                            title="Book a Free Outbound Triggers Demo"
                            subtitle="Tell us the event you want to dial on — we'll wire it up live"
                            submitLabel="Book Free Demo"
                        />
                    </motion.div>
                </section>

                <FAQSection
                    id="outbound-triggers"
                    eyebrow="Outbound Triggers · FAQ"
                    heading="Common questions about outbound triggers"
                    items={OUTBOUND_TRIGGERS_FAQS}
                />
            </main>
            <Footer />

            <JsonLd id="service-outbound-triggers" data={serviceSchema} />
        </div>
    );
}
